// Set or reset a password from an emailed link (`/reset#<token>`, ADR 0032). The token stays in
// the fragment so it never reaches a server log or a Referer.
import { createFileRoute } from "@tanstack/react-router";
import { SignInLayout } from "@vd/ui";
import { Check } from "lucide-react";
import { type FormEvent, useState } from "react";
import { ApiError, errorMessage } from "../api/client";
import { signin } from "../api/signin";
import { NewPasswordFields, passwordsMatch } from "../components/signin/NewPasswordFields";
import { Button } from "../components/ui";
import { useNavigate } from "../lib/navigate";

export const Route = createFileRoute("/reset")({
  staticData: { crumb: { label: "Set a password", parents: [] } },
  component: Reset,
});

function tokenFromHash(): string {
  return typeof window === "undefined" ? "" : decodeURIComponent(window.location.hash.replace(/^#/, ""));
}

function Reset() {
  const navigate = useNavigate();
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [state, setState] = useState<"form" | "saved" | "expired">(token ? "form" : "expired");

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!passwordsMatch(password, confirm)) return;
    setBusy(true);
    setProblem(null);
    try {
      await signin.reset(token, password, confirm);
      setState("saved");
      // The link is spent; keep it out of the history.
      window.history.replaceState(null, "", "/reset");
    } catch (err) {
      if (err instanceof ApiError && err.code === "link_expired") setState("expired");
      else setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SignInLayout>
      {state === "saved" ? (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="flex size-10 items-center justify-center rounded-pill bg-well text-success">
            <Check className="size-5" />
          </span>
          <p className="font-medium text-title">Password saved</p>
          <Button variant="primary" className="mt-2 w-full" onClick={() => void navigate({ to: "/signin" })}>
            Sign in
          </Button>
        </div>
      ) : state === "expired" ? (
        <div className="flex flex-col gap-4">
          <p className="font-medium text-title">This link has expired.</p>
          <Button className="w-full" onClick={() => void navigate({ to: "/signin" })}>
            Send a new link
          </Button>
        </div>
      ) : (
        <form onSubmit={save} className="flex flex-col gap-4">
          <h1 className="font-display font-medium text-display">Set a password</h1>
          <NewPasswordFields
            password={password}
            confirm={confirm}
            onPassword={setPassword}
            onConfirm={setConfirm}
            submitted={submitted}
            problem={problem}
            autoFocus
            size="lg"
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            busy={busy}
            disabled={busy || !password || !confirm}
          >
            Save
          </Button>
        </form>
      )}
    </SignInLayout>
  );
}
